import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Box, TextField, MenuItem, Alert, FormLabel, Typography } from '@mui/material';
import {
  FormShell,
  FormActions,
  SectionLabel,
  FieldLabel,
  EmptyOption,
  useLocationPicker,
  inputSx,
} from '../FormUI/RoadAccidentFormUI';
import useMunicipalityCascade from '../FormUI/useMunicipalityCascade';
import MunicipalityFields from '../FormUI/MunicipalityFields';

const backendUrl = process.env.REACT_APP_BACKEND_API_URL_LOCAL;

// Submitting a report now records who submitted it, so it requires a
// logged-in session - the same JWT every other authenticated call in this
// app already attaches.
const authHeaders = () => {
  const token = localStorage.getItem('access_token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

const initialFormData = {
  issue: '',
  other_issue: '',
  location: '',
  latitude: '',
  longitude: '',
  date_time: '',
  message: '',
};

function Reportroad() {
  const navigate = useNavigate();
  const cascade = useMunicipalityCascade();
  const [formData, setFormData] = useState(initialFormData);
  const [image, setImage] = useState(null);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState('');

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
      ...(name === 'issue' && value !== 'others' ? { other_issue: '' } : {}),
    }));
  };

  const handleLocationChange = (latitude, longitude) => {
    setFormData((prev) => ({ ...prev, latitude, longitude }));
  };

  const handleLocationResolved = (adm) => {
    if (adm) cascade.applyResolvedAdm(adm.adm1_code, adm.adm2_code, adm.adm3_code);
  };

  const handleLandmark = (landmark) => {
    setFormData((prev) => ({ ...prev, location: landmark || '' }));
  };

  const { latLngFields, mapElement, locationError, landmarkNotFound } = useLocationPicker({
    latitude: formData.latitude,
    longitude: formData.longitude,
    onChange: handleLocationChange,
    onLocationResolved: handleLocationResolved,
    onLandmark: handleLandmark,
    infoText: 'Click on the map to mark the road condition location for your report.',
  });

  const handleReset = () => {
    setFormData(initialFormData);
    setImage(null);
    cascade.reset();
    setSubmitSuccess(false);
    setSubmitError('');
  };

  const handleBackClick = () => {
    navigate('/Roadaccident/Reports?tab=roadcondition');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitError('');
    setSubmitSuccess(false);

    const dataToSend = new FormData();
    dataToSend.append('municipality', cascade.selectedMunicipality);
    dataToSend.append('submunicipality', cascade.selectedSubMunicipality);
    dataToSend.append('suco', cascade.selectedSuco);
    dataToSend.append('issue', formData.issue);
    if (formData.issue === 'others') {
      dataToSend.append('other_issue', formData.other_issue);
    }
    dataToSend.append('location', formData.location);
    dataToSend.append('latitude', formData.latitude);
    dataToSend.append('longitude', formData.longitude);
    dataToSend.append('date_time', formData.date_time);
    dataToSend.append('message', formData.message);
    if (image) {
      dataToSend.append('image', image);
    }

    try {
      const response = await axios.post(
        `${backendUrl}roadaccident/submit_report_road_condition/`,
        dataToSend,
        { headers: { 'Content-Type': 'multipart/form-data', ...authHeaders() } }
      );
      if (response.status === 200 || response.status === 201) {
        setSubmitSuccess(true);
        setTimeout(handleReset, 2000);
      } else {
        setSubmitError('Failed to submit report. Please try again.');
      }
    } catch (error) {
      const errorMessage = error.response?.data?.detail || JSON.stringify(error.response?.data) || error.message;
      setSubmitError(`An error occurred while submitting the report: ${errorMessage}`);
    }
  };

  return (
    <FormShell title="Report Road Condition" subtitle="Submit a road condition field report" onBack={handleBackClick} onSubmit={handleSubmit}>
      {submitSuccess && <Alert severity="success" sx={{ fontSize: '14px', borderRadius: '8px' }}>Road Condition Report submitted successfully.</Alert>}
      {submitError && <Alert severity="error" sx={{ fontSize: '14px', borderRadius: '8px' }}>{submitError}</Alert>}

      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '60% 40%' }, gap: 3 }}>
        {/* LEFT: Location Information */}
        <Box>
          <SectionLabel text="Location Information" />

          {locationError && !(cascade.selectedMunicipality && cascade.selectedSubMunicipality && cascade.selectedSuco) && (
            <Alert severity="warning" sx={{ fontSize: '13px', borderRadius: '8px', mb: 2 }}>{locationError}</Alert>
          )}

          <MunicipalityFields cascade={cascade} />
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Location"
              size="small"
              fullWidth
              sx={inputSx}
              name="location"
              value={formData.location}
              onChange={handleChange}
              placeholder="Enter location details"
            />
            {landmarkNotFound && !formData.location && (
              <Typography sx={{ fontSize: '12px', color: '#b45309', mt: 0.5 }}>No landmark found nearby — add one.</Typography>
            )}
          </Box>
          <Box sx={{ mt: 2.5 }}>
            {latLngFields}
          </Box>
        </Box>

        {/* RIGHT: Map */}
        <Box>{mapElement}</Box>
      </Box>

      <Box>
        <SectionLabel text="Issue Details" />
        <TextField
          label="Issue"
          select
          size="small"
          fullWidth
          sx={inputSx}
          name="issue"
          value={formData.issue}
          onChange={handleChange}
        >
          <EmptyOption text="Select an issue" />
          <MenuItem value="potholes">Potholes</MenuItem>
          <MenuItem value="speedbrakers">Speed brakers</MenuItem>
          <MenuItem value="sharpcurved">Sharp Curved</MenuItem>
          <MenuItem value="drain water flooding on road">Drain water flooding on road</MenuItem>
          <MenuItem value="unpaved road">Unpaved Road</MenuItem>
          <MenuItem value="others">Others</MenuItem>
        </TextField>

        {formData.issue === 'others' && (
          <Box sx={{ mt: 2.5 }}>
            <TextField
              label="Others"
              size="small"
              fullWidth
              sx={inputSx}
              name="other_issue"
              value={formData.other_issue}
              onChange={handleChange}
              placeholder="Describe the issue"
            />
          </Box>
        )}

        <Box sx={{ mt: 2.5 }}>
          <TextField
            label="Date and Time"
            type="datetime-local"
            size="small"
            fullWidth
            sx={inputSx}
            name="date_time"
            value={formData.date_time}
            onChange={handleChange}
            InputLabelProps={{ shrink: true }}
          />
        </Box>
      </Box>

      <Box>
        <SectionLabel text="Description" />
        <FieldLabel text="Message" />
        <TextField
          multiline
          rows={4}
          fullWidth
          sx={inputSx}
          name="message"
          value={formData.message}
          onChange={handleChange}
          placeholder="Write your message here"
        />
        <Box sx={{ mt: 2.5 }}>
          <FieldLabel text="Image" />
          <FormLabel
            component="label"
            sx={{
              display: 'inline-block', fontSize: '14px', color: '#374151',
              border: '1px solid #d1d5db', borderRadius: '8px', px: 2, py: 1, cursor: 'pointer',
              '&:hover': { borderColor: '#2563eb' },
            }}
          >
            {image ? image.name : 'Choose file'}
            <input hidden type="file" accept="image/*" onChange={(e) => setImage(e.target.files?.[0] || null)} />
          </FormLabel>
        </Box>
      </Box>

      <FormActions onReset={handleReset} submitLabel="Submit" />
    </FormShell>
  );
}

export default Reportroad;
